import {
  buildAttachmentUpdatePayload,
  useDeleteAttachment,
  useGetAllAttachments,
  useUpdateAttachment,
  type Attachment,
} from "@/api/attachment";
import { BoElementImagePickerBody } from "@/components/cms-puck/fields/BoElementImagePicker";
import {
  attachmentLargeThumb,
  attachmentThumb,
  attachmentTitle,
  catalogImageLabel,
  cmsMediaLibraryWhere,
} from "@/lib/cms-puck/media-utils";
import { getCatalogItemTypes } from "@/lib/cms-puck/blocks/selection";
import AddCroppedImageForm from "@/components/images/AddCroppedImageForm";
import { Col } from "@/components/form-items/AbstractForm";
import { MultilingualField } from "@/components/form-items/MultilingualField";
import StyledDrawer from "@/components/StyledDrawer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FormField, FormItem } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { hydrateAttachmentMultilingualFields } from "@/lib/media/attachment-multilingual";
import { stripExtension } from "@/lib/utils";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useQueryClient } from "@tanstack/react-query";
import { Clapperboard, Eye, Loader2, MoreHorizontal, MonitorPlay, Search, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import { AttachmentPixelSize } from "@/components/cms-puck/fields/AttachmentPixelSize";

/**
 * Section « Médias » de l'interface WordPress-like du CMS avancé
 * (`/cms/advanced/medias`) : la MÉDIATHÈQUE partagée (mêmes médias que les
 * pickers des blocs — `docId = CMS.id` + covers ancien CMS `docId = Instance.id`).
 *  - grille avec vignette / badge type (image, vidéo, embed) / légende ;
 *  - ajout via le « Unified CMS media form » (`AddCroppedImageForm`, EXACTEMENT
 *    le flux de l'onglet Téléverser du picker : docId=CMS.id + position
 *    `cmsPuckMedia` + création forcée d'un nouveau média) ;
 *  - suppression protégée : l'API refuse (409) si le média est référencé par
 *    une page Puck (garde BlockReference, même règle R10 que les œuvres).
 */

/** Position d'upload de la médiathèque CMS (KEEP IN SYNC MediaPickerField). */
const CMS_PUCK_MEDIA_POSITION = "cmsPuckMedia";

/** QueryKey de `useGetAllAttachments` (KEEP IN SYNC api/attachment.ts). */
const ATTACHMENTS_KEY = ["artworks", "attachments"];

function mediaTitle(att: Attachment): string {
  return attachmentTitle(att);
}

function mediaThumb(att: Attachment): string | undefined {
  return attachmentThumb(att);
}

function mediaPreviewUrl(att: Attachment): string | undefined {
  return attachmentLargeThumb(att) ?? attachmentThumb(att);
}

type BrowseDetail = {
  attachment: Attachment;
  element: {
    type: string;
    id: string;
    title: string;
    registrationNumber?: string;
  };
};

const KIND_PHRASE_BY_KIND: Record<string, "kindImage" | "kindVideo" | "kindEmbed"> = {
  image: "kindImage",
  video: "kindVideo",
  embed: "kindEmbed",
};

function MediaCard({
  att,
  onDelete,
  onView,
}: {
  att: Attachment;
  onDelete: (att: Attachment) => void;
  onView: (att: Attachment) => void;
}) {
  const cmsNew = useCmsNew();
  const [menuOpen, setMenuOpen] = useState(false);
  const url = mediaThumb(att);
  const title = mediaTitle(att);
  const kind = (att as { kind?: string }).kind ?? "image";
  const kindPhrase = KIND_PHRASE_BY_KIND[kind];
  const kindLabel = kindPhrase ? cmsPhrase(cmsNew, kindPhrase) : kind;

  return (
    <figure className="group relative overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="flex aspect-square w-full items-center justify-center overflow-hidden bg-slate-100">
        {url ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={url}
            alt={title}
            loading="lazy"
            className="max-h-full max-w-full object-contain"
          />
        ) : kind === "embed" ? (
          <MonitorPlay className="h-8 w-8 text-slate-400" />
        ) : (
          <Clapperboard className="h-8 w-8 text-slate-400" />
        )}
      </div>
      <figcaption className="flex flex-col gap-0.5 px-2 py-1.5">
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs text-slate-600" title={title}>
            {title}
          </span>
          {kind !== "image" ? (
            <Badge variant="secondary" className="shrink-0 text-[9px] uppercase">
              {kindLabel}
            </Badge>
          ) : null}
        </div>
        <AttachmentPixelSize
          att={att}
          className="truncate text-[10px] text-slate-400"
        />
      </figcaption>
      <div
        className={`absolute right-1.5 top-1.5 ${
          menuOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100 focus-within:opacity-100"
        }`}
      >
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title={cmsNew("cms.new.auto.title.actions", "Actions")}
              aria-label={cmsNew(
                "cms.new.auto.label.actions_sur_ce_media",
                "Actions sur ce média",
              )}
              className="rounded-md bg-white/90 p-1.5 text-slate-600 shadow-sm transition-colors hover:bg-white hover:text-slate-900"
            >
              <MoreHorizontal size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[10rem]">
            <DropdownMenuItem
              onSelect={() => onView(att)}
              className="gap-2 cursor-pointer"
            >
              <Eye size={14} />
              {cmsNew("cms.new.auto.text.voir", "Voir")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={() => onDelete(att)}
              className="gap-2 text-red-600 focus:text-red-600 cursor-pointer"
            >
              <Trash2 size={14} />
              {cmsNew("cms.new.auto.text.supprimer", "Supprimer")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </figure>
  );
}

type MediaSeoFormValues = {
  name: string;
  textAlt: Record<string, string>;
  caption: Record<string, string>;
  description: Record<string, string>;
};

function mediaSeoDefaults(att: Attachment | null): MediaSeoFormValues {
  if (!att) return { name: "", textAlt: {}, caption: {}, description: {} };
  const hydrated = hydrateAttachmentMultilingualFields(att);
  return {
    name: (att.name ?? "").trim() || stripExtension(mediaTitle(att)),
    textAlt: { ...(hydrated.textAlt ?? {}) },
    caption: { ...(hydrated.caption ?? {}) },
    description: { ...(hydrated.description ?? {}) },
  };
}

function MediaDetailDrawer({
  att,
  open,
  onOpenChange,
  onDelete,
  onSaved,
}: {
  att: Attachment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (att: Attachment) => void;
  onSaved: (att: Attachment) => void;
}) {
  const cmsNew = useCmsNew();
  const { t } = useTranslation();
  const { toast } = useToast();
  const updateAttachment = useUpdateAttachment();
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const form = useForm<MediaSeoFormValues>({
    defaultValues: mediaSeoDefaults(att),
  });

  useEffect(() => {
    if (!open || !att) return;
    form.reset(mediaSeoDefaults(att));
  }, [att, open, form]);

  const title = att ? mediaTitle(att) : "";
  const previewUrl = att ? mediaPreviewUrl(att) : undefined;
  const kind = (att as { kind?: string } | null)?.kind ?? "image";
  const kindPhrase = KIND_PHRASE_BY_KIND[kind];
  const kindLabel = kindPhrase ? cmsPhrase(cmsNew, kindPhrase) : kind;
  const createdAtRaw = (att as { createdAt?: string | Date } | null)?.createdAt;
  const createdAtLabel = createdAtRaw
    ? new Date(createdAtRaw).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  const saveSeo = form.handleSubmit((data) => {
    if (!att) return;
    const nextName = data.name.trim();
    updateAttachment.mutate(
      {
        id: att.id,
        attachment: buildAttachmentUpdatePayload(att, {
          name: nextName || att.name || "",
          textAlt: data.textAlt,
          caption: data.caption,
          description: data.description,
        }),
      },
      {
        onSuccess: () => {
          const next: Attachment = {
            ...att,
            name: nextName || att.name || "",
            textAlt: data.textAlt,
            caption: data.caption,
            description: data.description,
          };
          onSaved(next);
          form.reset({ ...data, name: next.name });
          toast({
            title: cmsNew(
              "cms.new.auto.title.media_enregistre",
              "Média enregistré",
            ),
            variant: "success",
            duration: 1600,
          });
        },
        onError: () => {
          toast({
            title: cmsNew(
              "cms.new.auto.title.echec_de_la_mise_a_jour",
              "Échec de la mise à jour",
            ),
            variant: "error",
            duration: 3000,
          });
        },
      },
    );
  });

  return (
    <>
      <StyledDrawer
        title={cmsNew("cms.new.auto.title.details_du_media", "Détails du média")}
        maxWidth="sm:max-w-md"
        sheetProps={{
          open,
          onOpenChange: (next) => {
            if (!next) setFullscreenOpen(false);
            onOpenChange(next);
          },
        }}
      >
        {att ? (
          <FormProvider {...form}>
            <div className="flex flex-col gap-5">
              <button
                type="button"
                disabled={!previewUrl}
                onClick={() => previewUrl && setFullscreenOpen(true)}
                className="group relative w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 disabled:cursor-default"
                title={
                  previewUrl
                    ? cmsNew(
                        "cms.new.auto.title.afficher_en_grand",
                        "Afficher en grand",
                      )
                    : undefined
                }
              >
                {previewUrl ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={previewUrl}
                    alt={title}
                    className="mx-auto max-h-80 w-full object-contain transition-opacity group-hover:opacity-95"
                  />
                ) : (
                  <p className="p-12 text-center text-sm text-slate-400">
                    {cmsPhrase(cmsNew, "previewUnavailable")}
                  </p>
                )}
                {previewUrl ? (
                  <span className="pointer-events-none absolute bottom-2 right-2 rounded-md bg-black/55 px-2 py-1 text-[10px] font-medium text-white opacity-0 transition-opacity group-hover:opacity-100">
                    {cmsNew("cms.new.auto.text.agrandir", "Agrandir")}
                  </span>
                ) : null}
              </button>

              <dl className="space-y-3 text-sm">
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {cmsNew("cms.new.auto.text.nom_du_fichier", "Nom du fichier")}
                  </dt>
                  <dd className="mt-1">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            id={field.name}
                            type="text"
                            placeholder={title}
                            aria-label={cmsNew(
                              "cms.new.auto.text.nom_du_fichier",
                              "Nom du fichier",
                            )}
                          />
                        </FormItem>
                      )}
                    />
                  </dd>
                </div>
                {createdAtLabel ? (
                  <div>
                    <dt className="text-xs font-medium text-slate-500">
                      {cmsNew("cms.new.auto.text.date_dupload", "Date d'upload")}
                    </dt>
                    <dd className="text-slate-800">{createdAtLabel}</dd>
                  </div>
                ) : null}
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {cmsNew(
                      "cms.new.auto.text.taille_en_pixels",
                      "Taille en pixels",
                    )}
                  </dt>
                  <dd className="text-slate-800">
                    <AttachmentPixelSize
                      att={att}
                      className="text-sm text-slate-800"
                    />
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-slate-500">
                    {cmsNew("cms.new.auto.text.type", "Type")}
                  </dt>
                  <dd className="text-slate-800">{kindLabel}</dd>
                </div>
              </dl>

              {/* SEO — mêmes champs que le formulaire d'ajout média (multilingue). */}
              <MultilingualField
                render={(lang) => (
                  <Col className="gap-3">
                    <FormField
                      control={form.control}
                      name={`textAlt.${lang}`}
                      render={({ field }) => (
                        <FormItem>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            label={t("addImageTextAltFR")}
                            id={field.name}
                            type="text"
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`caption.${lang}`}
                      render={({ field }) => (
                        <FormItem>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            label={t("addImageLegendFR")}
                            id={field.name}
                            type="text"
                          />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`description.${lang}`}
                      render={({ field }) => (
                        <FormItem>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            label={t("addImageDescriptionFR")}
                            id={field.name}
                            type="text"
                          />
                        </FormItem>
                      )}
                    />
                  </Col>
                )}
              />

              <Button
                type="button"
                className="w-full"
                disabled={updateAttachment.isPending}
                onClick={() => void saveSeo()}
              >
                {updateAttachment.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  cmsNew("cms.new.common.save", "Enregistrer")
                )}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="justify-start px-0 text-red-600 hover:bg-transparent hover:text-red-700"
                onClick={() => {
                  onOpenChange(false);
                  onDelete(att);
                }}
              >
                <Trash2 size={14} className="mr-2" />
                {cmsNew(
                  "cms.new.auto.text.supprimer_le_fichier",
                  "Supprimer le fichier",
                )}
              </Button>
            </div>
          </FormProvider>
        ) : null}
      </StyledDrawer>

      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent className="max-h-[95vh] max-w-[95vw] border-none bg-transparent p-2 shadow-none sm:p-4">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          {previewUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={previewUrl}
              alt={title}
              className="mx-auto max-h-[90vh] w-auto max-w-full object-contain"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function CMSAdvancedMedias() {
  const cmsNew = useCmsNew();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;
  const instanceId = instanceData?.id;

  const { data: attachments = [], isLoading } = useGetAllAttachments({
    where: cmsMediaLibraryWhere(cmsId, instanceId),
    order: { order: "asc" },
  });
  const deleteAttachment = useDeleteAttachment();

  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteFor, setDeleteFor] = useState<Attachment | null>(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [browseDetail, setBrowseDetail] = useState<BrowseDetail | null>(null);
  const [viewing, setViewing] = useState<Attachment | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = attachments as Attachment[];
    if (!q) return list;
    return list.filter((att) => mediaTitle(att).toLowerCase().includes(q));
  }, [attachments, search]);

  const refetch = () =>
    queryClient.invalidateQueries({ queryKey: ATTACHMENTS_KEY });

  const confirmDelete = () => {
    if (!deleteFor) return;
    deleteAttachment.mutate(deleteFor.id, {
      onSuccess: () => {
        setDeleteFor(null);
        setViewing(null);
        refetch();
        toast({ title: cmsNew("cms.new.auto.title.media_supprime", "Média supprimé"), variant: "success", duration: 1600 });
      },
      onError: (err: unknown) => {
        const res = (
          err as { response?: { status?: number; data?: { message?: string } } }
        )?.response;
        setDeleteFor(null);
        toast({
          // 409 = média référencé par une page Puck (garde BlockReference API).
          title:
            res?.status === 409
              ? (res.data?.message ?? cmsPhrase(cmsNew, "mediasDeleteBlocked"))
              : cmsPhrase(cmsNew, "mediasDeleteFailed"),
          variant: "error",
          duration: 4000,
        });
      },
    });
  };

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.medias", "Médias")}
      subtitle={cmsPhrase(cmsNew, "mediasSubtitle")}
      actions={
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setBrowseDetail(null);
              setCatalogOpen(true);
            }}
          >
            {cmsPhrase(cmsNew, "browseCatalog")}
          </Button>
          <Button size="sm" onClick={() => setUploadOpen(true)}>
            {cmsPhrase(cmsNew, "addMedia")}
          </Button>
        </div>
      }
    >
      <div className="px-6 py-4">
        <div className="relative mb-4 max-w-xs">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={cmsNew("cms.new.auto.placeholder.rechercher_un_media", "Rechercher un média…")}
            className="pl-8"
          />
        </div>

        {isLoading ? (
          <p className="py-12 text-center text-sm text-slate-400">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-slate-500">
              {search.trim()
                ? cmsPhrase(cmsNew, "mediasNoSearchResults")
                : cmsPhrase(cmsNew, "mediasEmpty")}
            </p>
            {!search.trim() ? (
              <Button size="sm" onClick={() => setUploadOpen(true)}>
                {cmsPhrase(cmsNew, "addFirstMedia")}
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6">
            {filtered.map((att) => (
              <MediaCard
                key={att.id}
                att={att}
                onDelete={setDeleteFor}
                onView={setViewing}
              />
            ))}
          </div>
        )}
      </div>

      <MediaDetailDrawer
        att={viewing}
        open={!!viewing}
        onOpenChange={(open) => {
          if (!open) setViewing(null);
        }}
        onDelete={setDeleteFor}
        onSaved={setViewing}
      />

      {/* Ajout — Unified CMS media form (image/vidéo/embed, multilingue). */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{cmsPhrase(cmsNew, "addMedia")}</DialogTitle>
            <DialogDescription>
              {cmsPhrase(cmsNew, "mediasAddDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          {cmsId ? (
            <AddCroppedImageForm
              docId={cmsId}
              imagePosition={CMS_PUCK_MEDIA_POSITION}
              refetch={refetch}
              forceCreateNewAttachment
              onSuccess={() => {
                refetch();
                setUploadOpen(false);
                toast({
                  title: cmsNew("cms.new.auto.title.media_ajoute", "Média ajouté"),
                  variant: "success",
                  duration: 1600,
                });
              }}
              buildImageName={(_instanceName, _docId, _position, _data, file) =>
                file ? stripExtension(file.name) : "embed"
              }
            />
          ) : (
            <p className="text-sm text-slate-500">
              {cmsPhrase(cmsNew, "mediasCmsNotFound")}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Suppression (garde API : 409 si utilisé par une page Puck). */}
      <Dialog
        open={!!deleteFor}
        onOpenChange={(open) => {
          if (!open) setDeleteFor(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{cmsNew("cms.new.auto.text.supprimer_le_media", "Supprimer le média")}</DialogTitle>
            <DialogDescription>
              {cmsPhrase(cmsNew, "mediasDeleteDialogDescription")}
              {deleteFor ? (
                <>
                  {" "}
                  « {mediaTitle(deleteFor)} »
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteFor(null)}>
              {cmsPhrase(cmsNew, "cancel")}
            </Button>
            <Button
              variant="destructive"
              disabled={deleteAttachment.isPending}
              onClick={confirmDelete}
            >
              {cmsPhrase(cmsNew, "delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parcourir le catalogue BO — outil de découverte (référence directe). */}
      <StyledDrawer
        title={cmsPhrase(cmsNew, "browseCatalog")}
        maxWidth="sm:max-w-2xl"
        sheetProps={{
          open: catalogOpen,
          onOpenChange: (open) => {
            setCatalogOpen(open);
            if (!open) setBrowseDetail(null);
          },
        }}
      >
        {browseDetail ? (
          <div className="space-y-4">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setBrowseDetail(null)}
            >
              {cmsPhrase(cmsNew, "backToSearch")}
            </Button>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
              {attachmentLargeThumb(browseDetail.attachment) ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={attachmentLargeThumb(browseDetail.attachment)}
                  alt={catalogImageLabel(browseDetail.attachment)}
                  className="mx-auto max-h-80 w-full object-contain"
                />
              ) : (
                <p className="p-8 text-center text-sm text-slate-400">
                  {cmsPhrase(cmsNew, "previewUnavailable")}
                </p>
              )}
            </div>
            <dl className="space-y-2 text-sm">
              <div>
                <dt className="text-xs font-medium text-slate-500">{cmsNew("cms.new.auto.text.element", "Élément")}</dt>
                <dd className="text-slate-800">
                  {[browseDetail.element.registrationNumber, browseDetail.element.title]
                    .filter(Boolean)
                    .join(" — ") || browseDetail.element.id}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{cmsNew("cms.new.auto.text.type", "Type")}</dt>
                <dd className="text-slate-800">
                  {getCatalogItemTypes().find(
                    (t) => t.type === browseDetail.element.type,
                  )?.label ?? browseDetail.element.type}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">{cmsPhrase(cmsNew, "mediaPosition")}</dt>
                <dd className="font-mono text-xs text-slate-700">
                  {browseDetail.attachment.position || "—"}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500">
                  {cmsPhrase(cmsNew, "mediaId")}
                </dt>
                <dd className="break-all font-mono text-xs text-slate-700">
                  {browseDetail.attachment.id}
                </dd>
              </div>
            </dl>
            <p className="text-xs text-slate-400">
              {cmsPhrase(cmsNew, "mediasCatalogBrowseHint")}
            </p>
          </div>
        ) : (
          <BoElementImagePickerBody
            browseOnly
            onBrowseSelect={({ attachment, element }) =>
              setBrowseDetail({ attachment, element })
            }
          />
        )}
      </StyledDrawer>
    </CmsAdminShell>
  );
}
